'use client';

/**
 * ReviewMarquee — the slow, endless, hand-movable row behind the review strip
 * (Senja sunset, 2026-09-26). Lars: "scrolling by slowly … when I hover it,
 * it stops, the hand appears, and they can actually crawl faster through it
 * with the hand. The moment I leave it, it starts to directly crawl slowly by
 * itself."
 *
 *   moves      requestAnimationFrame translate at `speed` px/s (Aria §3: 24px/s)
 *   hover      eases to a stop (Round 7: `stopMs`, the row's travel follows a
 *              cubic ease-out, so its speed falls as (1 − p)² and a quick
 *              fly-over only slows it); the cursor is the grab hand
 *              (reviews.css)
 *   drag       pointer drag moves the row, with momentum on release; the
 *              cursor is the closed hand while dragging
 *   leave      `resumeDelayMs` after the pointer leaves, the speed eases back
 *              in to cruise over `startMs` (smoothstep); the same ease-in
 *              follows a drag, a fling, a touch and keyboard focus
 *   keyboard   the row is focusable; focus inside it pauses it (WCAG 2.2.2),
 *              ←/→ step one card
 *   touch      swipe moves it (vertical page scroll is left to the browser)
 *   reduced    prefers-reduced-motion → no auto-scroll, no drag engine: a
 *   motion     plain horizontal scroller with one copy of the cards
 *
 * The cards are rendered twice (the second set aria-hidden, its controls out
 * of the tab order) so the row can wrap without a seam. Off screen, the loop
 * stops. A drag never turns into a click on the card under the pointer.
 */
import {
  Children,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** Something inside the row (a playing video) can hold it still. */
export const MarqueeHoldContext = createContext<(key: string, on: boolean) => void>(() => {});
/** true inside the duplicate set: its controls stay out of the tab order. */
export const MarqueeCloneContext = createContext(false);
export const useMarqueeHold = () => useContext(MarqueeHoldContext);
export const useIsMarqueeClone = () => useContext(MarqueeCloneContext);

const DRAG_THRESHOLD_PX = 5;
const MAX_FLING_PX_S = 2400;
const FRICTION_PER_S = 3.2; // momentum decay: v *= e^(-FRICTION·dt)

/**
 * Round 7 (Lars, 2026-09-27: "they should not [instantly stop] … more ease-out
 * stop … when I go, let them also then move quite quickly again"). The cruise
 * speed is scaled by a factor f in [0, 1] that is tweened, never switched:
 *   out  f = from · (1 − p)²          (the travel is a cubic ease-out to rest)
 *   in   f = from + (1 − from) · (3p² − 2p³)
 */
type Tween = { kind: 'out' | 'in'; from: number; t0: number; dur: number };
function tweenAt(tw: Tween, t: number): { f: number; done: boolean } {
  const p = tw.dur > 0 ? Math.min(1, Math.max(0, (t - tw.t0) / tw.dur)) : 1;
  if (tw.kind === 'out') return { f: tw.from * (1 - p) * (1 - p), done: p >= 1 };
  return { f: tw.from + (1 - tw.from) * p * p * (3 - 2 * p), done: p >= 1 };
}

export default function ReviewMarquee({
  children,
  speed = 24,
  direction = 'left',
  resumeDelayMs = 250,
  stopMs = 1500,
  startMs = 1000,
  label,
  stepPx = 340,
  className = '',
}: {
  children: ReactNode;
  speed?: number;
  direction?: 'left' | 'right';
  resumeDelayMs?: number;
  /** Round 7: hover → stop, eased over this many ms. */
  stopMs?: number;
  /** Round 7: → cruise again, eased over this many ms. */
  startMs?: number;
  label: string;
  stepPx?: number;
  className?: string;
}) {
  const vpRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const setRef = useRef<HTMLUListElement>(null);
  const [staticMode, setStaticMode] = useState(false);

  // Engine state lives in a ref: nothing here re-renders per frame.
  const st = useRef({
    x: 0,
    w: 0,
    hover: false,
    focus: false,
    touching: false,
    dragging: false,
    candidate: false,
    pointerId: -1,
    startX: 0,
    lastX: 0,
    lastT: 0,
    vel: 0,
    fling: 0,
    resumeAt: 0,
    suppressClick: false,
    holds: new Set<string>(),
    visible: true,
    raf: 0,
    prevT: 0,
    f: 1,
    tween: null as Tween | null,
  });

  const items = Children.toArray(children);

  const hold = useCallback((key: string, on: boolean) => {
    const s = st.current;
    if (on) s.holds.add(key);
    else {
      s.holds.delete(key);
      s.resumeAt = performance.now() + resumeDelayMs;
    }
  }, [resumeDelayMs]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setStaticMode(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const paint = useCallback(() => {
    const s = st.current;
    const track = trackRef.current;
    if (!track) return;
    if (s.w > 0) {
      while (s.x <= -s.w) s.x += s.w;
      while (s.x > 0) s.x -= s.w;
    }
    track.style.transform = `translate3d(${s.x.toFixed(2)}px,0,0)`;
  }, []);

  // Measure one set's width (the wrap distance).
  useEffect(() => {
    const set = setRef.current;
    if (!set || staticMode) return;
    const measure = () => {
      st.current.w = set.offsetWidth;
      paint();
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(set);
    return () => ro.disconnect();
  }, [staticMode, paint, items.length]);

  // The loop.
  useEffect(() => {
    const vp = vpRef.current;
    const track = trackRef.current;
    if (!vp || !track) return;
    const s = st.current;
    if (staticMode) {
      track.style.transform = '';
      return;
    }
    const dir = direction === 'left' ? -1 : 1;
    if (direction === 'right' && s.x === 0 && s.w > 0) s.x = -s.w / 2;

    const frame = (t: number) => {
      const dt = s.prevT ? Math.min(0.05, (t - s.prevT) / 1000) : 0;
      s.prevT = t;
      if (!s.dragging) {
        if (Math.abs(s.fling) > 8) {
          s.x += s.fling * dt;
          s.fling *= Math.exp(-FRICTION_PER_S * dt);
          if (Math.abs(s.fling) <= 8) s.resumeAt = Math.max(s.resumeAt, t + resumeDelayMs);
          s.f = 0;
          s.tween = null;
        } else {
          s.fling = 0;
          if (s.touching || s.holds.size > 0) {
            // A finger on the row or a playing video: held still, as before.
            s.f = 0;
            s.tween = null;
          } else if (s.hover || s.focus) {
            // Ease to a stop from wherever the speed is now.
            if (s.tween?.kind !== 'out') s.tween = s.f > 0 ? { kind: 'out', from: s.f, t0: t, dur: stopMs } : null;
          } else if (t >= s.resumeAt && s.f < 1 && s.tween?.kind !== 'in') {
            s.tween = { kind: 'in', from: s.f, t0: t, dur: startMs };
          }
          // While waiting out resumeDelayMs, a running ease-out keeps going.
          if (s.tween) {
            const r = tweenAt(s.tween, t);
            s.f = r.f;
            if (r.done) s.tween = null;
          }
          if (s.f > 0) s.x += dir * speed * s.f * dt;
        }
      } else {
        s.f = 0;
        s.tween = null;
      }
      paint();
      s.raf = s.visible && !document.hidden ? requestAnimationFrame(frame) : 0;
    };
    const start = () => {
      if (!s.raf && s.visible && !document.hidden) {
        s.prevT = 0;
        s.raf = requestAnimationFrame(frame);
      }
    };
    const stop = () => {
      if (s.raf) cancelAnimationFrame(s.raf);
      s.raf = 0;
    };

    const io = new IntersectionObserver((entries) => {
      s.visible = entries.some((e) => e.isIntersecting);
      if (s.visible) start();
      else stop();
    });
    io.observe(vp);
    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVis);
    start();

    return () => {
      stop();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [staticMode, direction, speed, resumeDelayMs, stopMs, startMs, paint]);

  // Pointer + focus + keyboard.
  useEffect(() => {
    const vp = vpRef.current;
    if (!vp || staticMode) return;
    const s = st.current;

    const onEnter = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') s.hover = true;
    };
    const onLeave = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') {
        s.hover = false;
        s.resumeAt = performance.now() + resumeDelayMs;
      }
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      s.candidate = true;
      s.dragging = false;
      s.pointerId = e.pointerId;
      s.startX = s.lastX = e.clientX;
      s.lastT = performance.now();
      s.vel = 0;
      s.fling = 0;
      if (e.pointerType !== 'mouse') s.touching = true;
      else s.hover = true; // a press is always inside the row
    };
    const onMove = (e: PointerEvent) => {
      if (!s.candidate || e.pointerId !== s.pointerId) return;
      if (!s.dragging) {
        if (Math.abs(e.clientX - s.startX) < DRAG_THRESHOLD_PX) return;
        s.dragging = true;
        vp.dataset.dragging = 'true';
        try {
          vp.setPointerCapture(e.pointerId);
        } catch {
          /* capture is an optimisation */
        }
      }
      const now = performance.now();
      const dx = e.clientX - s.lastX;
      const dt = Math.max(1, now - s.lastT) / 1000;
      s.x += dx;
      s.vel = 0.8 * (dx / dt) + 0.2 * s.vel;
      s.lastX = e.clientX;
      s.lastT = now;
      paint();
    };
    const end = (e: PointerEvent) => {
      if (e.pointerId !== s.pointerId) return;
      const now = performance.now();
      if (s.dragging) {
        // A pointer that stood still before release does not fling.
        const idle = now - s.lastT > 90;
        s.fling = idle ? 0 : Math.max(-MAX_FLING_PX_S, Math.min(MAX_FLING_PX_S, s.vel));
        s.suppressClick = true;
        window.setTimeout(() => (s.suppressClick = false), 0);
        delete vp.dataset.dragging;
      }
      s.dragging = false;
      s.candidate = false;
      s.pointerId = -1;
      if (e.pointerType !== 'mouse') s.touching = false;
      s.resumeAt = now + resumeDelayMs;
    };
    const onClickCapture = (e: MouseEvent) => {
      if (s.suppressClick) {
        e.preventDefault();
        e.stopPropagation();
        s.suppressClick = false;
      }
    };
    // Keyboard focus stops the row (WCAG 2.2.2); the focus a mouse press gives
    // the row does not — otherwise a drag would leave it paused until the
    // visitor clicked elsewhere.
    const onFocusIn = (e: FocusEvent) => {
      let keyboard = true;
      try {
        keyboard = (e.target as Element).matches(':focus-visible');
      } catch {
        keyboard = true;
      }
      if (keyboard) s.focus = true;
    };
    const onFocusOut = (e: FocusEvent) => {
      if (!vp.contains(e.relatedTarget as Node | null)) {
        s.focus = false;
        s.resumeAt = performance.now() + resumeDelayMs;
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.target !== vp) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        s.x += e.key === 'ArrowLeft' ? stepPx : -stepPx;
        paint();
      }
    };
    const onDragStart = (e: DragEvent) => e.preventDefault();

    vp.addEventListener('pointerenter', onEnter);
    vp.addEventListener('pointerleave', onLeave);
    vp.addEventListener('pointerdown', onDown);
    vp.addEventListener('pointermove', onMove);
    vp.addEventListener('pointerup', end);
    vp.addEventListener('pointercancel', end);
    vp.addEventListener('click', onClickCapture, true);
    vp.addEventListener('focusin', onFocusIn);
    vp.addEventListener('focusout', onFocusOut);
    vp.addEventListener('keydown', onKey);
    vp.addEventListener('dragstart', onDragStart);
    return () => {
      vp.removeEventListener('pointerenter', onEnter);
      vp.removeEventListener('pointerleave', onLeave);
      vp.removeEventListener('pointerdown', onDown);
      vp.removeEventListener('pointermove', onMove);
      vp.removeEventListener('pointerup', end);
      vp.removeEventListener('pointercancel', end);
      vp.removeEventListener('click', onClickCapture, true);
      vp.removeEventListener('focusin', onFocusIn);
      vp.removeEventListener('focusout', onFocusOut);
      vp.removeEventListener('keydown', onKey);
      vp.removeEventListener('dragstart', onDragStart);
    };
  }, [staticMode, resumeDelayMs, stepPx, paint]);

  return (
    <MarqueeHoldContext.Provider value={hold}>
      <div
        ref={vpRef}
        className={`rv-marquee ${className}`}
        role="region"
        aria-label={label}
        tabIndex={0}
        data-static={staticMode ? 'true' : undefined}
        data-testid="review-marquee"
      >
        <div ref={trackRef} className="rv-marquee__track">
          <ul ref={setRef} className="rv-marquee__set">
            {items.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
          <MarqueeCloneContext.Provider value={true}>
            <ul className="rv-marquee__set" aria-hidden="true">
              {items.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </MarqueeCloneContext.Provider>
        </div>
      </div>
    </MarqueeHoldContext.Provider>
  );
}
